/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import React from "react";
import { useNavigationWithProgress } from "@/hooks/utilities/use-navigation-with-progress";
import { logger } from "@/lib/utils/logger";
import { Button } from "@/components/ui/button";

export default function Error({
  error,
  reset,
}: {
  error: Error;
  reset: () => void;
}) {
  const router = useNavigationWithProgress();

  React.useEffect(() => {
    logger.error("Global error boundary caught error", {
      message: error.message,
      name: error.name,
      stack: error.stack,
    });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-wash px-4 py-12">
      <div className="w-full max-w-md rounded-[var(--radius-xl)] border border-[var(--border-hairline)] bg-surface-elevated p-6 text-center shadow-[var(--shadow-frame)]">
        <div
          className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-full border border-[var(--status-danger)] bg-error/10 text-lg font-bold text-error"
          aria-hidden
        >
          !
        </div>
        <h1 className="mb-2 text-base font-semibold text-text-primary">
          Something went wrong
        </h1>
        <p className="mb-4 text-sm text-text-secondary">
          An unexpected error occurred. You can retry or return to the
          dashboard.
        </p>
        {process.env.NODE_ENV === "development" && (
          <details className="mb-4 text-left text-xs" open>
            <summary className="cursor-pointer text-text-muted">
              Error Details (dev only)
            </summary>
            <pre className="mt-2 max-h-40 overflow-auto rounded-[var(--radius-md)] border border-[var(--border-hairline)] bg-surface-frame p-2 text-xs text-text-secondary">
              {error.message}
              {"\n\n"}
              {error.stack}
            </pre>
          </details>
        )}
        <div className="flex items-center justify-center gap-2">
          <Button onClick={() => reset()} variant="default" size="sm">
            Try Again
          </Button>
          <Button
            onClick={() => router.push("/app/dashboard")}
            variant="outline"
            size="sm"
          >
            Go Home
          </Button>
        </div>
      </div>
    </div>
  );
}
